import Input from "components/input"
import { SwitchButton } from "components/switchButton"
import { Permissions } from "../permissions"
import { HttpError, useOne } from "@refinedev/core";
import { IPermission, IAdminAccountRoleForm, IAdminAccountRoleDetail } from "interfaces";
import Button from "components/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Label } from "components/label";
import { FullScreenLoading } from "components/fullScreenLoading";
import { get, isEmpty } from "lodash";
import { useEffect } from "react";

interface AdminAccountFormProps {
  onSubmit: (data: IAdminAccountRoleForm) => void;
  defaultValue?: IAdminAccountRoleDetail;
}

const AdminAccountRoleFormSchema = z.object({
  is_active: z.boolean(),
  name: z.string().min(1, "This field is required").max(50, "Text length is too long"),
  description: z.string().max(100, "Text length is too long"),
  permissions: z.array(z.object({
    resource: z.string(),
    app: z.string(),
    actions: z.array(z.string()),
    name: z.string()
  }))
}).superRefine(({ permissions }, ctx) => {
  if (permissions.every(item => item.actions.length === 0)) {
    ctx.addIssue({
      path: ['permissions'],
      code: "custom",
      message: "Action is required"
    });
  }
});

export const AdminAccountRoleForm = ({onSubmit, defaultValue}: AdminAccountFormProps) => {

  const { 
    register,
    resetField,
    reset,
    formState: { errors },
    control,
    watch,
    handleSubmit
  } = useForm<IAdminAccountRoleForm, HttpError, IAdminAccountRoleForm>({
    defaultValues: {
      is_active: defaultValue?.role.is_active ?? false,
      description: defaultValue?.role.description ?? "",
      name: defaultValue?.role.name ?? "",
      permissions: defaultValue?.role_permission ?? []
    },
    resolver: zodResolver(AdminAccountRoleFormSchema)
  });

  const {
    data: permissions,
    isFetching: isFetchingPermissions,
  } = useOne<IPermission[]>({
    resource: "admin-account/permissions",
    id: "maximum",
    meta: {
      resource: "data",
      filters: [
        {
          field: "scope",
          operator: "eq",
          value: "admin",
        },
      ],
    },
    queryOptions: {
      enabled: true
    } as any,
    successNotification: (data, values, resource) => {
      const permissionMaximumData = (get(data, "data", []) ?? []);
      resetField("permissions", {
        defaultValue: (permissionMaximumData ?? []).map((permission) => ({
          ...permission,
          actions:
            (defaultValue?.role_permission ?? []).find(
              (p) =>
                p.resource === permission.resource && p.app === permission.app
            )?.actions ?? [],
        })),
      });

      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    }
  });

  useEffect(() => {
    if (!isEmpty(defaultValue)) {
      reset({
        is_active: defaultValue?.role.is_active ?? false,
        description: defaultValue?.role.description ?? "",
        name: defaultValue?.role.name ?? "",
        permissions: defaultValue?.role_permission ?? []
      })
    }
  }, [defaultValue, reset])

  const permissionsData = permissions?.data ?? [];

  const _submit = (values: IAdminAccountRoleForm) => {
    onSubmit({...values});
  }

  return (
    <form className="w-full flex flex-col gap-y-3" onSubmit={handleSubmit(_submit)}>
      <div className="divide-y divide-gray-100 box-container p-0">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid gap-x-4 gap-y-4 sm:grid-cols-6 lg:gap-x-6">
            <div className="sm:col-span-1">
              <Label 
                required
                id="status" 
                name="Status"  
              />
              <Controller
                control={control}
                name="is_active"
                render={({ field: { onChange, value = true, ref } }) => (
                  <SwitchButton checked={value} onChange={onChange} />
                )}
              />
            </div>
            <div className="sm:col-span-2">
              <Label 
                required
                id="name" 
                name="Role name"  
              />
              <Input 
                id="name"
                type="text"
                placeholder="Role name"
                maxLength={50}
                error={errors["name"]}
                {...register("name")}
              />
              <div className="flex justify-between flex-row-reverse">
                <p className="text-xs leading-6">{(watch("name") ?? "").length}/50</p>
                  {errors["name"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["name"].message}
                    </span>
                  </p> 
                ): null}
              </div>
            </div>
            <div className="sm:col-span-3">
              <Label 
                id="description" 
                name="Role description"  
              />
              <Input 
                id="description"
                type="text"
                placeholder="Role description"
                maxLength={100}
                error={errors["description"]}
                {...register("description")}
              />
              <div className="flex justify-between flex-row-reverse">
                <p className="text-xs leading-6">{(watch("description") ?? "").length}/100</p>
                  {errors["description"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["description"].message}
                    </span>
                  </p> 
                ): null}
              </div>
            </div>
            <div className="col-span-full">
              <Label 
                required
                id="permissions" 
                name="Access Permission"
              />
              <Controller
                control={control}
                name="permissions"
                render={({ field: { onChange, value } }) => (
                  <Permissions 
                    permissionMaximumData={permissionsData}
                    data={value}
                    onChange={(data: IPermission[]) => onChange(data)}
                    key={JSON.stringify(defaultValue?.role_permission)}
                  />
                )}
              />
              {errors["permissions"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["permissions"]?.message}
                  </span>
                </p> 
              ): null}
            </div>
          </div>
        </div>
      </div>
      <div className="w-full px-4 py-4 sm:px-6 bg-white">
        <div className="flex items-center justify-end gap-x-4">
          <Link to={`/admin-accounts/roles${defaultValue ? `/${defaultValue.role.id}` : ""}`}>
            <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
              CANCEL
            </Button>
          </Link>
          <Button className="w-20" type="submit">
            SAVE
          </Button>
        </div>
      </div>
      {isFetchingPermissions ? <FullScreenLoading /> : <></>}
    </form>
  )
} 